// Setting up the built-in booking system: what the business sells, and when it
// is open. Without both, availability is empty forever and the agent tells every
// caller there is nothing free.
import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, Plus, Trash2, Loader2, AlertCircle, X, Clock } from 'lucide-react';
import {
  builtinBookingService, BuiltinService, BuiltinHours, BuiltinAppointment,
} from 'api/services/crmProviders.service';

const DAYS: BuiltinHours['dayOfWeek'][] =
  ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const pretty = (d: string) => d.charAt(0) + d.slice(1).toLowerCase();

/** 150 → "2h 30m". Durations are the thing this screen is really about. */
const humanDuration = (mins?: number) => {
  if (!mins || mins <= 0) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const BookingSetup: React.FC = () => {
  const [services, setServices] = useState<BuiltinService[]>([]);
  const [hours, setHours] = useState<BuiltinHours[]>([]);
  const [diary, setDiary] = useState<BuiltinAppointment[]>([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<BuiltinService | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [s, h] = await Promise.all([builtinBookingService.services(), builtinBookingService.hours()]);
    if (s.success) setServices(s.data || []); else setError(s.error || 'Failed to load services');
    if (h.success) setHours(h.data || []);
    setLoading(false);
  }, []);

  const loadDiary = useCallback(async (d: string) => {
    const res = await builtinBookingService.appointments(d);
    if (res.success) setDiary(res.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDiary(date); }, [date, loadDiary]);

  const saveService = async () => {
    if (!editing) return;
    if (!editing.name?.trim()) { setError('A service needs a name'); return; }
    if (!editing.durationMinutes || editing.durationMinutes <= 0) {
      // Refused rather than defaulted: every availability calculation is built
      // on this, and a wrong one produces no slots or overlapping ones.
      setError('How long does it take? Availability is worked out from the duration.');
      return;
    }
    const res = await builtinBookingService.saveService(editing);
    if (!res.success) { setError(res.error || 'Could not save'); return; }
    setEditing(null); setError(null); load();
  };

  const setDay = async (day: BuiltinHours['dayOfWeek'], opensAt: string, closesAt: string, id?: string) => {
    if (opensAt >= closesAt) {
      setError(`${pretty(day)}: closing time must be after opening time`);
      return;
    }
    const res = await builtinBookingService.saveHours({ id, dayOfWeek: day, opensAt, closesAt });
    if (!res.success) { setError(res.error || 'Could not save hours'); return; }
    setError(null); load();
  };

  /** Drops one window — a business ending just its evening shift. */
  const removeWindow = async (id: string) => {
    await builtinBookingService.removeHoursWindow(id);
    await load();
  };

  const closeDay = async (day: string) => {
    await builtinBookingService.closeDay(day);
    load();
  };

  /**
   * Every window open on that day, earliest first.
   *
   * A day can have more than one: salons and clinics here routinely shut for
   * lunch, and 09:00-13:00 plus 16:00-20:00 could not be said at all when this
   * was one row per day — the only way to express it was 09:00-20:00, which
   * offered appointments at a shop with the shutters down.
   */
  const windowsFor = (d: string) =>
    hours.filter(h => h.dayOfWeek === d).sort((a, b) => (a.opensAt || '').localeCompare(b.opensAt || ''));

  const input = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm';
  const lbl = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

  const noServices = !loading && services.length === 0;
  const noHours = !loading && hours.length === 0;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
          <CalendarClock className="w-5 h-5" style={{ color: 'var(--accent)' }} />
          Booking setup
        </h1>
        <p className="text-[13px] mt-1 max-w-2xl" style={{ color: 'var(--text-muted)' }}>
          For businesses using the built-in booking system. The agent works out what is free
          from your services and opening hours — a 30-minute haircut and a 4-hour treatment
          get different times offered.
        </p>
      </div>

      {/* Both are required, and it is not obvious why — so say it rather than
          leaving someone wondering why the agent never offers anything. */}
      {(noServices || noHours) && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl px-3 py-2 flex gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            {noServices && noHours && 'Add your services and opening hours — until both exist the agent tells every caller nothing is available.'}
            {noServices && !noHours && 'No services yet, so the agent has nothing to book.'}
            {!noServices && noHours && 'No opening hours yet, so every day counts as closed.'}
          </span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          {/* Without this the banner is a dead end. load() runs on mount only,
              so a momentary failure — a backend restart, a dropped connection —
              leaves a red box sitting there permanently, describing something
              that stopped being true seconds later. That is indistinguishable
              from a real outage to the person looking at it. */}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="underline underline-offset-2 font-medium disabled:opacity-50 whitespace-nowrap"
          >
            {loading ? 'Retrying…' : 'Try again'}
          </button>
        </div>
      )}

      {/* services */}
      <div className="tl-card">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-semibold text-gray-900 dark:text-white">Services</h2>
          <button className="tl-btn tl-btn-primary tl-btn-sm"
                  onClick={() => setEditing({ name: '', durationMinutes: 30, concurrentCapacity: 1, enabled: true })}>
            <Plus className="w-3.5 h-3.5" /> Add service
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : services.length === 0 ? (
          <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>Nothing yet.</p>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {services.map(s => (
              <div key={s.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{s.name}</div>
                  <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                    {humanDuration(s.durationMinutes)}{s.price ? ` · ${s.price}` : ''}{(s.concurrentCapacity ?? 1) > 1 ? ` · ${s.concurrentCapacity} at once` : ''}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => setEditing(s)}>Edit</button>
                  <button className="tl-icon-btn"
                          onClick={async () => { if (s.id) { await builtinBookingService.removeService(s.id); load(); } }}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* opening hours */}
      <div className="tl-card">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-1">Opening hours</h2>
        <p className="text-[12px] mb-3" style={{ color: 'var(--text-muted)' }}>
          A day with no hours is treated as closed. Add a second row for a day that
          shuts for lunch — the agent will not offer anything in the gap.
        </p>
        <div className="space-y-2">
          {DAYS.map(d => {
            const ws = windowsFor(d);
            return (
              <div key={d} className="flex items-start gap-2 flex-wrap">
                <span className="w-24 pt-2 text-sm text-gray-700 dark:text-gray-300">{pretty(d)}</span>
                <div className="flex-1 space-y-1.5">
                  {ws.map(h => (
                    <div key={h.id} className="flex items-center gap-2 flex-wrap">
                      <input type="time" defaultValue={h.opensAt?.slice(0, 5)} className={input + ' w-28'}
                             onBlur={e => setDay(d, e.target.value, h.closesAt.slice(0, 5), h.id)} />
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>to</span>
                      <input type="time" defaultValue={h.closesAt?.slice(0, 5)} className={input + ' w-28'}
                             onBlur={e => setDay(d, h.opensAt.slice(0, 5), e.target.value, h.id)} />
                      <button className="tl-icon-btn" title="Remove this time"
                              onClick={() => removeWindow(h.id!)}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    {ws.length > 0 ? (
                      <>
                        <button className="tl-btn tl-btn-secondary tl-btn-sm"
                                onClick={() => setDay(d, '16:00', '20:00')}>
                          <Plus className="w-3.5 h-3.5" /> Add a second time
                        </button>
                        <button className="text-xs hover:underline" style={{ color: 'var(--text-muted)' }}
                                onClick={() => closeDay(d)}>
                          Closed all day
                        </button>
                      </>
                    ) : (
                      <button className="tl-btn tl-btn-secondary tl-btn-sm"
                              onClick={() => setDay(d, '09:00', '19:00')}>
                        <Clock className="w-3.5 h-3.5" /> Open this day
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* the diary */}
      <div className="tl-card">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h2 className="font-semibold text-gray-900 dark:text-white">Appointments</h2>
          <input type="date" className={input + ' w-44'} value={date} onChange={e => setDate(e.target.value)} />
        </div>
        {diary.length === 0 ? (
          <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>Nothing booked that day.</p>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {diary.map(a => (
              <div key={a.reference} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {a.startsAt?.slice(11, 16)} · {a.serviceName || 'Appointment'}
                  </div>
                  <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                    {a.customerName || a.customerPhone} · {a.customerPhone}
                  </div>
                </div>
                <span className="tl-pill font-mono">{a.reference}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'var(--scrim)' }}>
          <div className="tl-card w-full max-w-md">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editing.id ? 'Edit service' : 'Add service'}
              </h2>
              <button className="tl-icon-btn" onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className={lbl}>Name</label>
                <input className={input} value={editing.name}
                       onChange={e => setEditing({ ...editing, name: e.target.value })}
                       placeholder="e.g. Haircut, Keratin treatment" />
              </div>
              <div>
                <label className={lbl}>How long it takes (minutes)</label>
                <input type="number" min={5} step={5} className={input}
                       value={editing.durationMinutes || ''}
                       onChange={e => setEditing({ ...editing, durationMinutes: parseInt(e.target.value, 10) })} />
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  Everything depends on this — {humanDuration(editing.durationMinutes)} decides which
                  times the agent can offer, and stops a long treatment being booked too near closing.
                </p>
              </div>
              <div>
                <label className={lbl}>How many at the same time</label>
                <input type="number" min={1} step={1} className={input}
                       value={editing.concurrentCapacity ?? 1}
                       onChange={e => setEditing({ ...editing,
                         concurrentCapacity: Math.max(1, parseInt(e.target.value, 10) || 1) })} />
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {(editing.concurrentCapacity ?? 1) > 1
                    ? `The agent will offer the same time to ${editing.concurrentCapacity} callers before it counts as full.`
                    : 'One at a time — a booking closes that slot. Raise it if you have more than one chair, room or therapist.'}
                </p>
              </div>
              <div>
                <label className={lbl}>Price <span style={{ color: 'var(--text-muted)' }}>— optional</span></label>
                <input className={input} value={editing.price || ''}
                       onChange={e => setEditing({ ...editing, price: e.target.value })} placeholder="₹800" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="tl-btn tl-btn-neutral" onClick={() => setEditing(null)}>Cancel</button>
              <button className="tl-btn tl-btn-primary" onClick={saveService}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingSetup;
